/* eslint-disable @angular-eslint/prefer-inject */
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import {
    ShipmentFromPackingListsViewModel,
    ShipmentsService,
} from '@control-tower/platform-wms';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    IPagedResult,
    PFWmsEntities,
} from '@pf/shared-common';
import { ShipmentStore } from './shipment.store';
import {
    ShipmentCreate,
    ShipmentRead,
    ShipmentSearchParams,
} from '../../entities/shipment.dto';
import { Operation } from 'fast-json-patch';
import { asArraySafe } from '@pf/shared-utility';
import { InventoryBundleStore } from '../inventory-bundles/inventory-bundle.store';
import { ContainerStore } from '../containers/container.store';
import { ContainerRead } from '../../entities/container.dto';
import { InventoryBundleRead } from '../../entities/inventory-bundle.dto';
import { PackLineStore } from '../pack-lines/pack-line.store';
import { PackLineRead } from '../../entities/pack-line.dto';

@Injectable()
export class ShipmentDataService extends AbstractDataService<
    ShipmentRead,
    ShipmentCreate
> {
    private readonly bundleStore = inject(InventoryBundleStore);
    private readonly containerStore = inject(ContainerStore);
    private readonly packLineStore = inject(PackLineStore);

    protected EntityName = PFWmsEntities.Shipment;
    constructor(
        private service: ShipmentsService,
        store: ShipmentStore,
        logger: LoggerService
    ) {
        super(store, logger);
    }
    protected createObs$(body: ShipmentCreate): Observable<ShipmentRead> {
        return this.service.shipmentCreate({
            shipmentViewModelCreateBody: body,
        }) as Observable<ShipmentRead>;
    }
    protected deleteObs$(id: string): Observable<ShipmentRead> {
        return this.service.shipmentDelete({
            id,
        }) as Observable<ShipmentRead>;
    }
    protected undoDeleteObs$(id: string): Observable<ShipmentRead> {
        return this.service.shipmentUndoDelete({
            id,
        }) as Observable<ShipmentRead>;
    }
    protected getObs$(id: string): Observable<ShipmentRead> {
        return this.service.shipmentReadById({
            id,
        }) as Observable<ShipmentRead>;
    }
    protected updateObs$(
        id: string,
        operations: Operation[]
    ): Observable<ShipmentRead> {
        return this.service.shipmentUpdatePatch({
            id,
            operation: operations,
        }) as Observable<ShipmentRead>;
    }
    protected searchObs$(
        searchParams: ShipmentSearchParams
    ): Observable<IPagedResult<ShipmentRead>> {
        return this.service.shipmentSearch(searchParams) as Observable<
            IPagedResult<ShipmentRead>
        >;
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<ShipmentRead>>> {
        return this.service.shipmentGetChanges(searchParams) as Observable<
            IPagedResult<EntityChangeTrackingDto<ShipmentRead>>
        >;
    }

    createFromPackingLists(request: ShipmentFromPackingListsViewModel) {
        return this.service
            .shipmentCreateShipmentFromPackList({
                shipmentFromPackingListsViewModel: request,
            })
            .pipe(
                map(result => {
                    const shipment = result.shipment as ShipmentRead;
                    this.store.add(shipment, false);
                    return shipment;
                })
            );
    }

    ship$(shipmentId: string) {
        return this.service
            .shipmentShip({
                id: shipmentId,
            })
            .pipe(map(result => this.applyShipmentResult(result)));
    }

    cancel$(shipmentId: string) {
        return this.service
            .v1ShipmentsIdCancelPost({
                id: shipmentId,
            })
            .pipe(map(result => this.applyShipmentResult(result)));
    }

    private applyShipmentResult(result: {
        affectedContainers?: unknown[] | null;
        affectedBundles?: unknown[] | null;
        affectedPackLines?: unknown[] | null;
        shipment?: unknown;
    }): ShipmentRead {
        asArraySafe(result.affectedContainers).forEach(container => {
            this.containerStore.update(container as ContainerRead);
        });

        asArraySafe(result.affectedBundles).forEach(bundle => {
            this.bundleStore.update(bundle as InventoryBundleRead);
        });

        asArraySafe(result.affectedPackLines).forEach(packLine => {
            this.packLineStore.update(packLine as PackLineRead);
        });

        const shipment = result.shipment as ShipmentRead;
        result.shipment && this.store.add(shipment, false);

        return shipment;
    }
}
